import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getCustomerPortalWorkorder, toPortalErrorMessage } from "../../services/customerPortalApi";

export default function PortalWorkorderDetailPage() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        setLoading(true);
        setError("");
        const res = await getCustomerPortalWorkorder(id);
        if (!active) return;
        setItem(res?.item || null);
      } catch (err) {
        if (!active) return;
        setError(toPortalErrorMessage(err, "Werkbon detail laden mislukt"));
      } finally {
        if (active) setLoading(false);
      }
    }

    if (id) {
      load();
    } else {
      setLoading(false);
      setError("Werkbon niet gevonden");
    }

    return () => {
      active = false;
    };
  }, [id]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-blue-50 px-4 py-6 sm:px-6">
      <div className="mx-auto max-w-6xl space-y-4">
        <header className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <nav className="mb-2 text-xs text-slate-500">
            <Link to="/portaal" className="hover:underline">Dashboard</Link>
            <span className="px-1">/</span>
            <Link to="/portaal/werkbonnen" className="hover:underline">Werkbonnen</Link>
            <span className="px-1">/</span>
            <span className="font-semibold text-slate-700">Detail</span>
          </nav>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Klantportaal</p>
          <h1 className="text-2xl font-bold text-slate-900">Werkbon detail</h1>
        </header>

        {loading ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-500 shadow-sm">Laden...</div>
        ) : error ? (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>
        ) : !item ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-500 shadow-sm">Werkbon niet gevonden.</div>
        ) : (
          <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-base font-semibold text-slate-900">{item.workorderNumber || "-"}</h2>
              <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-700">
                {item.statusLabel || formatStatus(item.status)}
              </span>
            </div>
            <div className="grid gap-2 text-sm text-slate-700 md:grid-cols-2">
              <p><span className="text-slate-500">Titel:</span> {item.title || "-"}</p>
              <p><span className="text-slate-500">Project:</span> {item.projectNumber || "-"}</p>
              <p><span className="text-slate-500">Datum:</span> {formatDate(item.plannedDate)}</p>
              <p><span className="text-slate-500">Locatie:</span> {item.locationLabel || "-"}</p>
            </div>
            <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
              {item.description || "Geen aanvullende omschrijving beschikbaar."}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value || "-");
  return new Intl.DateTimeFormat("nl-NL", { dateStyle: "medium" }).format(date);
}

function formatStatus(status) {
  const value = String(status || "").trim().toLowerCase();
  if (!value) return "-";
  return value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}
